"use client";

import { useEffect, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { motion } from "framer-motion";
import { stegaClean } from "@sanity/client/stega";
import { cn } from "@/lib/utils";
import type {
  ITradingCompetitionColumns,
  ITradingCompetitionPrizeBand,
  ITradingCompetitionYou,
} from "@/sanity/pages/PageTradingCompetitionTemplate";
import { METAL } from "./PodiumCard";
import { formatDelta, formatPoints, rankLabel } from "./useLiveBoard";
import {
  formatPrize,
  isEmail,
  maskName,
  nextPlaceProgress,
  prizeForRank,
  publicPoints,
  type BoardRow,
  type BoardSource,
  type RankHit,
} from "./board";

/*
 * Figma "Email search" (10604:162): desktop search / result in the top 100 / result outside it / not
 * found = 10604:274 / 563 / 852 / 1141; mobile search / inside / outside = 10608:1737 / 1982 / 2301.
 */

// ── Copy ─────────────────────────────────────────────────────────────────
// Hardcoded EN/DE until the board has CMS fields (the page has no BR version).

const COPY = {
  en: {
    title: "Find your rank",
    hint: "Enter the email of your IQ Capital account to see where you stand.",
    placeholder: "Your email address",
    button: "Show my rank",
    again: "Search again",
    yourRank: "YOUR RANK",
    position: "POSITION",
    notFound: "No account on the board with this email. Check the spelling or use the email you trade with.",
    invalid: "Enter a valid email address.",
    topOnly: "Top 100 only",
    noPrize: "No prize",
  },
  de: {
    title: "Finde deinen Platz",
    hint: "Gib die E-Mail deines IQ Capital Kontos ein, um zu sehen, wo du stehst.",
    placeholder: "Deine E-Mail-Adresse",
    button: "Platz anzeigen",
    again: "Neue Suche",
    yourRank: "DEIN PLATZ",
    position: "PLATZ",
    notFound:
      "Kein Konto mit dieser E-Mail auf dem Board. Prüfe die Schreibweise oder nutze die E-Mail, mit der du tradest.",
    invalid: "Gib eine gültige E-Mail-Adresse ein.",
    topOnly: "Nur Top 100",
    noPrize: "Kein Preis",
  },
};

// ── Icons (Figma's Carbon exports, without their multiply rect) ──────────

const ICONS = {
  search: (
    <path
      d="M18.125 17.2412L13.4049 12.5211C14.5391 11.1595 15.1047 9.41298 14.984 7.64495C14.8633 5.87693 14.0656 4.22349 12.7569 3.02861C11.4482 1.83373 9.7292 1.1894 7.95751 1.22966C6.18582 1.26992 4.49786 1.99167 3.24477 3.24477C1.99167 4.49786 1.26992 6.18582 1.22966 7.95751C1.1894 9.7292 1.83373 11.4482 3.02861 12.7569C4.22349 14.0656 5.87693 14.8633 7.64495 14.984C9.41298 15.1047 11.1595 14.5391 12.5211 13.4049L17.2412 18.125L18.125 17.2412ZM2.50001 8.12501C2.50001 7.01249 2.82991 5.92495 3.44799 4.99992C4.06607 4.0749 4.94458 3.35393 5.97241 2.92818C7.00025 2.50244 8.13125 2.39105 9.22239 2.60809C10.3135 2.82513 11.3158 3.36086 12.1025 4.14753C12.8892 4.9342 13.4249 5.93648 13.6419 7.02762C13.859 8.11877 13.7476 9.24977 13.3218 10.2776C12.8961 11.3054 12.1751 12.1839 11.2501 12.802C10.3251 13.4201 9.23753 13.75 8.12501 13.75C6.63367 13.7484 5.20389 13.1552 4.14936 12.1007C3.09482 11.0461 2.50166 9.61634 2.50001 8.12501Z"
      fill="#A3DEB2"
    />
  ),
  email: (
    <path
      d="M14 3H2C1.73478 3 1.48043 3.10536 1.29289 3.29289C1.10536 3.48043 1 3.73478 1 4V12C1 12.2652 1.10536 12.5196 1.29289 12.7071C1.48043 12.8946 1.73478 13 2 13H14C14.2652 13 14.5196 12.8946 14.7071 12.7071C14.8946 12.5196 15 12.2652 15 12V4C15 3.73478 14.8946 3.48043 14.7071 3.29289C14.5196 3.10536 14.2652 3 14 3ZM12.9 4L8 7.39L3.1 4H12.9ZM2 12V4.455L7.715 8.41C7.7987 8.46806 7.89813 8.49918 8 8.49918C8.10187 8.49918 8.2013 8.46806 8.285 8.41L14 4.455V12H2Z"
      fill="#F2F7F5"
    />
  ),
  close: (
    <path
      d="M12 4.7L11.3 4L8 7.3L4.7 4L4 4.7L7.3 8L4 11.3L4.7 12L8 8.7L11.3 12L12 11.3L8.7 8L12 4.7Z"
      fill="#F2F7F5"
      fillOpacity="0.8"
    />
  ),
  arrow: (
    <path
      d="M9 3L8.285 3.6965L12.075 7.5H2V8.5H12.075L8.285 12.2865L9 13L14 8L9 3Z"
      fill="#001B14"
    />
  ),
  warning: (
    <>
      <path
        d="M8 1C6.61553 1 5.26215 1.41054 4.11101 2.17971C2.95986 2.94888 2.06266 4.04213 1.53284 5.32122C1.00303 6.6003 0.864407 8.00776 1.1345 9.36563C1.4046 10.7235 2.07129 11.9708 3.05025 12.9497C4.02922 13.9287 5.2765 14.5954 6.63437 14.8655C7.99224 15.1356 9.3997 14.997 10.6788 14.4672C11.9579 13.9373 13.0511 13.0401 13.8203 11.889C14.5895 10.7378 15 9.38447 15 8C15 6.14348 14.2625 4.36301 12.9497 3.05025C11.637 1.7375 9.85652 1 8 1ZM8 14C6.81331 14 5.65327 13.6481 4.66658 12.9888C3.67988 12.3295 2.91085 11.3925 2.45672 10.2961C2.0026 9.19974 1.88378 7.99334 2.11529 6.82946C2.3468 5.66557 2.91824 4.59647 3.75736 3.75736C4.59648 2.91824 5.66557 2.3468 6.82946 2.11529C7.99335 1.88378 9.19974 2.0026 10.2961 2.45672C11.3925 2.91085 12.3295 3.67988 12.9888 4.66658C13.6481 5.65327 14 6.81331 14 8C14 9.5913 13.3679 11.1174 12.2426 12.2426C11.1174 13.3679 9.5913 14 8 14Z"
        fill="#D39A6A"
      />
      <path
        d="M8.5 4H7.5V9.5H8.5V4Z"
        fill="#D39A6A"
      />
      <path
        d="M8 11C7.85166 11 7.70666 11.044 7.58332 11.1264C7.45999 11.2088 7.36386 11.3259 7.30709 11.463C7.25033 11.6 7.23547 11.7508 7.26441 11.8963C7.29335 12.0418 7.36478 12.1754 7.46967 12.2803C7.57456 12.3852 7.7082 12.4566 7.85368 12.4856C7.99917 12.5145 8.14997 12.4997 8.28701 12.4429C8.42406 12.3861 8.54119 12.29 8.6236 12.1667C8.70601 12.0433 8.75 11.8983 8.75 11.75C8.75 11.5511 8.67098 11.3603 8.53033 11.2197C8.38968 11.079 8.19891 11 8 11Z"
        fill="#D39A6A"
      />
    </>
  ),
};

function Icon({
  name,
  size,
  className,
  style,
}: {
  name: keyof typeof ICONS;
  size: 16 | 20;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      aria-hidden="true"
      className={cn("block shrink-0", className)}
      style={style}>
      {ICONS[name]}
    </svg>
  );
}

// ── Table ────────────────────────────────────────────────────────────────

const TEXT = "font-geist [line-height:normal] whitespace-nowrap";
const ROW = "flex items-center gap-[1rem] px-[1.5rem] lg:gap-[2rem] lg:px-[3rem]";
const COL_RANK = "w-[9.875rem] shrink-0 lg:w-[11rem]";
const COL_POINTS = "w-[8rem] shrink-0 text-right lg:w-[20rem]";
const COL_LAST = "hidden w-[17.5rem] shrink-0 text-right lg:block";
const COL_PRIZE = "w-[9rem] shrink-0 text-right lg:w-[17.5rem]";

/** Rows next to the searched one stay readable: one row either side on mobile, two on desktop. */
function dimFor(distance: number) {
  if (distance === 1) return "opacity-60";
  if (distance === 2) return "opacity-25 lg:opacity-60";
  return "opacity-25";
}

function TraderRow({
  row,
  prize,
  dim,
  you,
  badge,
  copy,
}: {
  row: BoardRow;
  prize?: ReactNode;
  dim?: string;
  /** The visitor's own row: painted white. */
  you?: boolean;
  badge?: string;
  copy: (typeof COPY)["en"];
}) {
  const metal = !you && row.rank <= 3 ? METAL[row.rank as 1 | 2 | 3] : undefined;
  return (
    <div
      data-rank={row.rank}
      aria-current={you ? "true" : undefined}
      className={cn(
        ROW,
        "relative h-[7.5rem] w-full shrink-0 transition-opacity duration-500",
        you ? "bg-white" : "border-t border-solid border-[rgba(214,238,231,0.1)]",
        dim,
      )}>
      <div className={cn("flex items-center", COL_RANK)}>
        <div
          className={cn(
            "flex h-[4.5rem] w-[7.5rem] items-center justify-center rounded-full lg:w-[8rem]",
            you ? "bg-[#001b14]" : metal ? "border border-solid bg-white/[0.06]" : "bg-[rgba(214,238,231,0.06)]",
          )}
          style={metal ? { borderColor: `rgba(${metal},0.5)` } : undefined}>
          <p
            className={cn(
              TEXT,
              "mb-0 text-[1.75rem] font-medium",
              you ? "text-white" : !metal && "text-[rgba(214,238,231,0.66)]",
            )}
            style={metal ? { color: `rgb(${metal})` } : undefined}>
            {rankLabel(row.rank)}
          </p>
        </div>
      </div>

      {/* Names stay private on the live board: first name only ("Kevin"), and the badge on the visitor's own row */}
      <div className="flex min-w-0 flex-1 items-center gap-[1.5rem]">
        {you ? (
          badge && (
            <p className={cn(TEXT, "mb-0 py-[0.25rem] text-[2rem] font-semibold text-[#01140f] lg:text-black")}>
              {badge}
            </p>
          )
        ) : (
          // Figma's name slot (drawn at opacity 0 there): 14px / 16px Medium
          <p className={cn(TEXT, "mb-0 min-w-0 truncate text-[1.75rem] font-medium text-[#f2f7f5] lg:text-[2rem]")}>
            {maskName(row.name)}
          </p>
        )}
      </div>

      <p
        className={cn(
          TEXT,
          COL_POINTS,
          "mb-0 text-[1.75rem] font-medium lg:text-[2rem]",
          you ? "text-[#001b14]" : "text-[#f2f7f5]",
        )}>
        {formatPoints(publicPoints(row))}
      </p>
      <p
        className={cn(
          TEXT,
          COL_LAST,
          "mb-0 text-[1.75rem] font-medium",
          you
            ? row.lastDay < 0
              ? "text-[#a3501c]"
              : "text-[#086044]"
            : row.lastDay < 0
              ? "text-[#ffb36b]"
              : "text-[#12f0b4]",
        )}>
        {formatDelta(row.lastDay)}
      </p>
      {prize || !you ? (
        <p
          className={cn(
            TEXT,
            COL_PRIZE,
            "mb-0 text-[1.75rem] font-semibold lg:text-[2rem]",
            you ? "text-[#001b14]" : !metal && "text-[#f2f7f5]",
          )}
          style={metal ? { color: `rgb(${metal})` } : undefined}>
          {prize}
        </p>
      ) : (
        <p className={cn(TEXT, COL_PRIZE, "mb-0 text-[1.75rem] font-medium text-[rgba(0,27,20,0.6)]")}>
          <span className="lg:hidden">{copy.noPrize}</span>
          <span className="hidden lg:inline">{copy.topOnly}</span>
        </p>
      )}
      {metal && (
        <i
          aria-hidden="true"
          className="absolute top-[-0.125rem] left-0 h-[7.5rem] w-[0.5rem]"
          style={{ background: `rgb(${metal})` }}
        />
      )}
    </div>
  );
}

/** The traders between the top list and the visitor's own row. */
function GapRow() {
  return (
    <div
      aria-hidden="true"
      className="relative h-[4.5rem] w-full shrink-0 lg:h-[5rem]">
      <div className="absolute top-1/2 left-[3.75rem] flex -translate-y-1/2 gap-[0.75rem] lg:left-[6.5rem]">
        {[0, 1, 2].map((i) => (
          <i
            key={i}
            className="block h-[0.5rem] w-[0.5rem] rounded-full bg-white/[0.35]"
          />
        ))}
      </div>
    </div>
  );
}

// ── Finder card ──────────────────────────────────────────────────────────

function Chip({ label, value, color = "#f2f7f5" }: { label?: string; value: string; color?: string }) {
  return (
    <div className="flex shrink-0 items-center gap-[0.75rem] overflow-clip rounded-[0.25rem] border border-solid border-white/10 bg-white/5 px-[1.25rem] py-[0.625rem]">
      <p className="mb-0 font-medium text-[rgba(242,247,245,0.66)]">{label}</p>
      <p
        className="mb-0 font-semibold"
        style={{ color }}>
        {value}
      </p>
    </div>
  );
}

function SearchAgain({ onClick, label, className }: { onClick: () => void; label: string; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-[5rem] shrink-0 items-center gap-[1rem] overflow-clip rounded-[0.25rem] border border-solid border-white/[0.18] px-[1.75rem] transition-colors hover:bg-white/5 lg:px-[2rem]",
        className,
      )}>
      <Icon
        name="close"
        size={16}
        className="h-[2rem] w-[2rem]"
      />
      <span className={cn(TEXT, "text-[1.75rem] font-medium text-[#f2f7f5]")}>{label}</span>
    </button>
  );
}

type Status = "idle" | "invalid" | "searching" | "found" | "notFound";

interface Props {
  source: BoardSource;
  bands: ITradingCompetitionPrizeBand[];
  columns?: ITradingCompetitionColumns;
  you?: ITradingCompetitionYou;
  note?: string;
  language?: string;
}

/**
 * The live board: the "Find your rank" card over the top 100. A hit turns the card into the result
 * (rank, progress to the next place up, points / last day / prize) and glides the table to the
 * visitor's row, painted white, with the rest of the board faded back. A rank outside the top 100 is
 * appended under the list after a gap.
 */
export default function LiveBoard({ source, bands, columns, you, note, language }: Props) {
  const copy = COPY[stegaClean(language) === "de" ? "de" : "en"];

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [hit, setHit] = useState<RankHit | null>(null);
  const request = useRef(0);
  const timer = useRef<number>();
  const cardRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const top = source.top;
  const outside = hit && hit.rank > top.length ? hit : null;
  const error = status === "invalid" ? copy.invalid : status === "notFound" ? copy.notFound : null;

  const search = async (e: FormEvent) => {
    e.preventDefault();
    const email = query.trim();
    if (!isEmail(email)) {
      setStatus("invalid");
      return;
    }
    const id = ++request.current;
    setStatus("searching");
    const found = await source.lookup(email);
    if (id !== request.current) return;
    setHit(found);
    setStatus(found ? "found" : "notFound");
  };

  const reset = () => {
    request.current++;
    window.clearTimeout(timer.current);
    setHit(null);
    setStatus("idle");
    boxRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
  };

  // Custom scrollbar (desktop): Figma's 4px track and fixed 56px thumb, following the board's scroll.
  const syncThumb = () => {
    const box = boxRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!box || !track || !thumb) return;
    const max = box.scrollHeight - box.clientHeight;
    const travel = track.clientHeight - thumb.offsetHeight;
    thumb.style.transform = `translateY(${max > 0 ? (box.scrollTop / max) * travel : 0}px)`;
  };

  useEffect(() => () => window.clearTimeout(timer.current), []);

  /**
   * On a hit: scroll the page only as far as needed to show the card and the visitor's row, then glide
   * the board to the row (Figma: four rows above it on desktop, two on mobile; the end of the list
   * outside the top 100). Two smooth scrolls at once cancel each other in Chrome, hence the order.
   */
  useEffect(() => {
    if (!hit) return;
    const card = cardRef.current;
    const box = boxRef.current;
    const row = box?.querySelector<HTMLElement>(`[data-rank="${hit.rank}"]`);
    if (!card || !box || !row) return;
    syncThumb();

    const desktop = window.matchMedia("(min-width: 993px)").matches;
    const max = box.scrollHeight - box.clientHeight;
    const boxTarget = outside ? max : Math.min(max, Math.max(0, row.offsetTop - row.offsetHeight * (desktop ? 4 : 2)));

    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const rowBottom =
      box.getBoundingClientRect().top + window.scrollY + row.offsetTop - boxTarget + row.offsetHeight + 2 * rem;
    const cardTop = card.getBoundingClientRect().top + window.scrollY - 10 * rem; // clear of the sticky nav
    let pageTarget = window.scrollY;
    if (rowBottom > pageTarget + window.innerHeight) pageTarget = rowBottom - window.innerHeight;
    if (cardTop < pageTarget) pageTarget = Math.max(cardTop, rowBottom - window.innerHeight);

    const glide = () => {
      // a far row: jump to a few rows short of it, so the visible glide stays short
      const lead = row.offsetHeight * 6;
      if (Math.abs(boxTarget - box.scrollTop) > lead)
        box.scrollTop = boxTarget + (boxTarget > box.scrollTop ? -lead : lead);
      box.scrollTo({ top: boxTarget, behavior: "smooth" });
    };
    const scrolls = Math.abs(pageTarget - window.scrollY) > 8;
    if (scrolls) window.scrollTo({ top: pageTarget, behavior: "smooth" });
    timer.current = window.setTimeout(glide, scrolls ? 650 : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hit]);

  // Fade by distance from the visitor's row, counted in rows of the list (the gap does not count).
  const youIndex = hit ? (outside ? top.length : hit.rank - 1) : -1;
  const prizeOf = (rank: number) => {
    const p = prizeForRank(bands, rank);
    return p ? formatPrize(p.amount) : undefined;
  };
  const prize = hit ? prizeOf(hit.rank) : undefined;
  const progress = hit ? nextPlaceProgress(hit) : 0;

  return (
    <div className="flex w-full flex-col items-center gap-[2rem] lg:w-[150rem]">
      <div
        ref={cardRef}
        className="w-full">
        {hit ? (
          // ── Result ──────────────────────────────────────────────────────
          <motion.div
            key="result"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            className="flex w-full flex-col gap-[2rem] overflow-clip rounded-[0.5rem] border border-solid border-[rgba(18,240,180,0.16)] bg-white/5 p-[2rem] lg:flex-row lg:items-center lg:gap-[4rem] lg:px-[3.5rem] lg:py-[3rem]">
            <div className="flex w-full items-start justify-between lg:w-auto">
              <div className="flex shrink-0 flex-col items-start font-geist font-medium whitespace-nowrap">
                {/* heights = Figma's text boxes, which round up to whole px (52.8 → 53, 61.6 → 62) */}
                <p className="mb-0 h-[2.25rem] text-[1.75rem] leading-[normal] text-[rgba(242,247,245,0.66)]">
                  {copy.yourRank}
                </p>
                <p className="mb-0 h-[6.625rem] text-[6rem] leading-[1.1] tracking-[-0.18rem] text-[#f2f7f5] lg:h-[7.75rem] lg:text-[7rem] lg:tracking-[-0.21rem]">
                  {hit.rank}
                </p>
              </div>
              <SearchAgain
                onClick={reset}
                label={copy.again}
                className="flex lg:hidden"
              />
            </div>

            <i
              aria-hidden="true"
              className="hidden h-[9rem] w-px shrink-0 bg-white/[0.12] lg:block"
            />

            <div className="flex min-w-0 flex-col items-start gap-[2rem] lg:flex-1 lg:gap-[1.5rem]">
              {you?.badge && (
                <div className="flex items-center overflow-clip rounded-[0.25rem] border border-solid border-white/10 bg-white/5 px-[1rem] py-[0.25rem]">
                  <p className={cn(TEXT, "mb-0 text-[1.75rem] font-semibold text-[#f2f7f5]")}>{you.badge}</p>
                </div>
              )}
              <div className="relative h-[0.5rem] w-[44.75rem] max-w-full overflow-clip rounded-[0.25rem] bg-white/10 lg:w-[45rem]">
                <motion.i
                  className="absolute top-0 left-0 block h-full bg-[#a3deb2]"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress * 100}%` }}
                  transition={{ delay: 0.2, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
            </div>

            <div className="flex w-full items-center gap-[1rem] font-geist text-[1.75rem] leading-[normal] whitespace-nowrap lg:w-auto lg:shrink-0">
              <Chip
                label={columns?.points}
                value={formatPoints(publicPoints(hit))}
              />
              <Chip
                label={columns?.lastDay}
                value={formatDelta(hit.lastDay)}
                color={hit.lastDay < 0 ? "#ffb36b" : "#12f0b4"}
              />
              {prize && (
                <Chip
                  label={columns?.prize}
                  value={prize}
                />
              )}
            </div>

            <SearchAgain
              onClick={reset}
              label={copy.again}
              className="hidden lg:flex"
            />
          </motion.div>
        ) : (
          // ── Search (and not found) ──────────────────────────────────────
          <form
            onSubmit={search}
            role="search"
            noValidate
            className={cn(
              "flex w-full flex-col items-start gap-[2rem] overflow-clip rounded-[0.5rem] border border-solid bg-white/5 p-[2.5rem] lg:px-[3.5rem] lg:py-[3rem]",
              error ? "border-[rgba(18,240,180,0.16)]" : "border-white/[0.14]",
            )}>
            <div className="flex w-full flex-col items-start gap-[2rem] lg:flex-row lg:items-center lg:justify-between">
              <div className="flex w-full flex-col items-start gap-[2rem] lg:w-auto lg:gap-[0.75rem]">
                <label
                  htmlFor="tc-rank-email"
                  className="mb-0 flex items-center gap-[1.25rem]">
                  <Icon
                    name="search"
                    size={20}
                    className="h-[2.5rem] w-[2.5rem]"
                  />
                  {/* line heights = Figma's text boxes: 26px title, 18px lines */}
                  <span className="font-geist text-[2.5rem] leading-[3.25rem] font-medium whitespace-nowrap text-[#f2f7f5]">
                    {copy.title}
                  </span>
                </label>
                <p className="mb-0 font-geist text-[1.75rem] leading-[2.25rem] font-normal text-[rgba(242,247,245,0.66)] lg:whitespace-nowrap">
                  {copy.hint}
                </p>
              </div>

              <div className="flex w-full flex-col gap-[2rem] lg:w-auto lg:flex-row lg:items-center lg:gap-[1rem]">
                <div
                  className={cn(
                    "flex h-[6rem] w-full items-center gap-[1.25rem] overflow-clip rounded-[0.25rem] border border-solid bg-white/[0.04] px-[2rem] lg:w-[50rem]",
                    error ? "border-[rgba(211,154,106,0.9)]" : "border-white/[0.18]",
                  )}>
                  <Icon
                    name="email"
                    size={16}
                    className="h-[2rem] w-[2rem]"
                    style={{ opacity: query ? 0.8 : 0.5 }}
                  />
                  <input
                    ref={inputRef}
                    id="tc-rank-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    spellCheck={false}
                    value={query}
                    placeholder={copy.placeholder}
                    aria-invalid={!!error}
                    aria-describedby={error ? "tc-rank-error" : undefined}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      if (status === "invalid" || status === "notFound") setStatus("idle");
                    }}
                    // 16px minimum on phones, or iOS zooms the page on focus
                    className="h-full min-w-0 flex-1 bg-transparent font-geist text-[max(16px,2rem)] leading-[normal] font-normal text-[#f2f7f5] outline-none placeholder:text-[rgba(242,247,245,0.42)] lg:text-[2rem]"
                  />
                </div>
                <button
                  type="submit"
                  aria-busy={status === "searching"}
                  className={cn(
                    "flex h-[6rem] w-full shrink-0 items-center justify-center gap-[1rem] overflow-clip rounded-[0.25rem] bg-white px-[2.5rem] transition-opacity hover:opacity-90 lg:w-auto",
                    status === "searching" && "opacity-70",
                  )}>
                  <span className={cn(TEXT, "text-[2rem] font-medium text-[#001b14]")}>{copy.button}</span>
                  <Icon
                    name="arrow"
                    size={16}
                    className="h-[2rem] w-[2rem] lg:hidden"
                  />
                </button>
              </div>
            </div>

            {error && (
              <div
                id="tc-rank-error"
                role="alert"
                className="flex w-full items-center gap-[1rem] overflow-clip rounded-[0.25rem] border border-solid border-[rgba(211,154,106,0.35)] bg-[rgba(211,154,106,0.1)] px-[2rem] py-[1.5rem]">
                <Icon
                  name="warning"
                  size={16}
                  className="h-[2rem] w-[2rem]"
                />
                <p className="mb-0 font-geist text-[1.75rem] leading-[normal] font-medium text-[rgba(242,247,245,0.9)]">
                  {error}
                </p>
              </div>
            )}
          </form>
        )}
      </div>

      {/* ── Board ──────────────────────────────────────────────────────── */}
      <div className="group relative flex w-full flex-col overflow-clip rounded-[0.5rem] border border-solid border-[rgba(214,238,231,0.1)] bg-[rgba(214,238,231,0.02)]">
        <div
          className={cn(
            ROW,
            TEXT,
            "h-[6rem] w-full shrink-0 bg-[rgba(214,238,231,0.03)] text-[1.75rem] font-medium tracking-[0.105rem] text-[rgba(242,247,245,0.42)]",
          )}>
          <p className={cn("mb-0", COL_RANK)}>{copy.position}</p>
          <p className="mb-0 min-w-0 flex-1">{columns?.trader}</p>
          <p className={cn("mb-0", COL_POINTS)}>{columns?.points}</p>
          <p className={cn("mb-0", COL_LAST)}>{columns?.lastDay}</p>
          <p className={cn("mb-0", COL_PRIZE)}>{columns?.prize}</p>
        </div>

        <div
          ref={boxRef}
          onScroll={syncThumb}
          className={cn(
            "relative flex w-full flex-col overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
            outside ? "h-[34.5rem] lg:h-[72.5rem]" : hit ? "h-[37.5rem] lg:h-[75rem]" : "h-[23.375rem] lg:h-[75rem]",
          )}>
          {top.map((row, i) =>
            hit && row.id === hit.id ? (
              <TraderRow
                key={row.id}
                row={row}
                prize={prizeOf(row.rank)}
                you
                badge={you?.badge}
                copy={copy}
              />
            ) : (
              <TraderRow
                key={row.id}
                row={row}
                prize={prizeOf(row.rank)}
                dim={hit ? dimFor(Math.abs(i - youIndex)) : undefined}
                copy={copy}
              />
            ),
          )}
          {outside && (
            <>
              <GapRow />
              <TraderRow
                row={outside}
                you
                badge={you?.badge}
                copy={copy}
              />
            </>
          )}
        </div>

        {/* Figma draws the track once a rank is shown; before that it appears while the board is hovered */}
        <div
          ref={trackRef}
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute top-[6.75rem] right-[0.75rem] bottom-[1rem] hidden w-[0.5rem] rounded-[0.25rem] bg-white/[0.06] transition-opacity duration-300 lg:block",
            hit ? "opacity-100" : "opacity-0 group-hover:opacity-100",
          )}>
          <div
            ref={thumbRef}
            className="absolute top-0 left-0 h-[7rem] w-full rounded-[0.25rem] bg-white/[0.35]"
          />
        </div>
      </div>

      {note && (
        <p className="mb-0 w-full text-center font-geist text-[1.75rem] leading-[normal] font-normal text-[rgba(242,247,245,0.42)] lg:w-auto lg:whitespace-nowrap">
          {note}
        </p>
      )}
    </div>
  );
}
